<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import AllFiltersItem from './AllFiltersItem.vue';
    import dayjs from 'dayjs';
    import { computed } from 'vue';
    import DateRangeInput from '../DateRangeInput.vue';
    import EchoButton from '../EchoButton.vue';
    import { CalendarIcon } from '@lucide/vue';
    import { defaultDateRange, startOfWeekMonday } from '@/js/utils/dateUtils';

    const props = defineProps<{
        start: string | null;
        end: string | null;
    }>();

    const emit = defineEmits<{
        (e: 'change', value: { start: string; end: string }): void;
    }>();

    // Controlled component: getter derives display value from props, setter emits to parent.
    // This prevents prop-driven updates from leaking back as emits and causing feedback loops.
    const range = computed({
        get: () => {
            const fallback = defaultDateRange();
            return {
                start: props.start ? dayjs(props.start).format('YYYY-MM-DD') : fallback.start,
                end: props.end ? dayjs(props.end).format('YYYY-MM-DD') : fallback.end,
            };
        },
        set: (newValue: { start: string; end: string }) => {
            emit('change', newValue);
        },
    });

    const setToYesterday = () =>
        emit('change', {
            start: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            end: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
        });

    const setToLastDays = (days: number) =>
        emit('change', {
            start: dayjs().subtract(days, 'day').add(1, 'day').format('YYYY-MM-DD'),
            end: dayjs().format('YYYY-MM-DD'),
        });

    const setToLastMonths = (months: number) =>
        emit('change', {
            start: dayjs().subtract(months, 'month').add(1, 'day').format('YYYY-MM-DD'),
            end: dayjs().format('YYYY-MM-DD'),
        });

    const setToMonthToDate = () =>
        emit('change', {
            start: dayjs().startOf('month').format('YYYY-MM-DD'),
            end: dayjs().format('YYYY-MM-DD'),
        });

    const setToCurrentWeek = () =>
        emit('change', {
            start: startOfWeekMonday().format('YYYY-MM-DD'),
            end: dayjs().format('YYYY-MM-DD'),
        });
</script>

<template>
    <AllFiltersItem>
        <template #icon><CalendarIcon class="size-5" /></template>
        {{ __('date') }}

        <template #content>
            <div class="px-4 pt-4 pb-6">
                <DateRangeInput v-model="range" />

                <h4 class="Input-label capitalize mt-5 mb-2">
                    {{ __('predefined-intervals') }}
                </h4>
                <div class="flex gap-1.5 flex-wrap">
                    <EchoButton variant="primary" size="small" @click="setToYesterday()">
                        {{ __('yesterday') }}
                    </EchoButton>
                    <EchoButton variant="primary" size="small" @click="setToLastDays(7)">
                        {{ __('last-7-days') }}
                    </EchoButton>
                    <EchoButton variant="primary" size="small" @click="setToCurrentWeek()">
                        {{ __('this-week') }}
                    </EchoButton>
                    <EchoButton variant="primary" size="small" @click="setToMonthToDate()">
                        {{ __('month-to-date') }}
                    </EchoButton>
                    <EchoButton variant="primary" size="small" @click="setToLastMonths(3)">
                        {{ __('last-3-months') }}
                    </EchoButton>
                    <EchoButton variant="primary" size="small" @click="setToLastMonths(6)">
                        {{ __('last-6-months') }}
                    </EchoButton>
                </div>
            </div>
        </template>
    </AllFiltersItem>
</template>
